import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Plus, Search, Globe, Edit2, Eye, Settings, Trash2, Archive,
  ExternalLink, MoreHorizontal, LayoutGrid, List, X, Star
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { formatRelativeTime } from '../../utils/format'
import { SkeletonGrid } from '../common/Skeleton'
import { NSConfirm } from '../shared/ns-confirm'
import SiteThumbPreview from '../shared/SiteThumbPreview'
import NewSite from './NewSite'

// ── Helpers ──────────────────────────────────────────────────────────────────

const getSiteEditorLogo = (site) => {
  try {
    const raw = site?.meta_data
    if (!raw) return null
    const meta = typeof raw === 'string' ? JSON.parse(raw) : raw
    return meta?.logo || null
  } catch { return null }
}

// Derive a stable gradient from site id/name
const GRADIENTS = [
  ['#7C5CFF', '#5B7CFF'],
  ['#A78BFA', '#7C5CFF'],
  ['#EC4899', '#A78BFA'],
  ['#FF8A65', '#F76C5E'],
  ['#2BD9A6', '#06B6D4'],
  ['#06B6D4', '#5B7CFF'],
  ['#FFB454', '#FF8A65'],
  ['#5BC0EB', '#5B7CFF'],
  ['#2BD9A6', '#FFB454'],
]

function siteGradient(site) {
  const hash = (site.id || '').toString().split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return GRADIENTS[hash % GRADIENTS.length]
}

const STATUS_CONFIG = {
  published: { tone: 'success', label: 'Live', dot: true },
  draft:     { tone: '',        label: 'Draft' },
  archived:  { tone: 'info',   label: 'Archived' },
}

// Real visits over the last 30 days (site_visits via the sites API).
const formatVisits = (n) => {
  const v = Number(n) || 0
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k visits`
  return `${v} ${v === 1 ? 'visit' : 'visits'}`
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SiteThumb({ site }) {
  const [from, to] = siteGradient(site)
  const logo = getSiteEditorLogo(site)
  const sb = STATUS_CONFIG[site.status] || STATUS_CONFIG.draft
  const pillBg = sb.tone === 'success'
    ? 'rgba(43,217,166,0.85)'
    : sb.tone === 'info'
    ? 'rgba(91,192,235,0.75)'
    : 'rgba(7,8,15,0.65)'

  return (
    <div className="site-thumb" style={{ background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}>
      {/* Live snapshot of the real home page; falls back to the gradient + faux
          preview below when the site has no content yet. */}
      <SiteThumbPreview html={site.preview_html} css={site.preview_css} />
      {!site.preview_html && (
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 12, left: 14, right: 14, display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.4)' }} />
            <span style={{ width: 6, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.4)' }} />
            <span style={{ width: 6, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.4)' }} />
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 9, color: 'rgba(255,255,255,0.6)' }}>
              {site.name}
            </span>
          </div>
          {logo ? (
            <img src={logo} alt={site.name} style={{ position: 'absolute', bottom: 20, left: 14, width: 48, height: 48, objectFit: 'contain' }} />
          ) : (
            <>
              <div style={{ position: 'absolute', left: 14, top: 36, width: 70, height: 8, background: 'rgba(255,255,255,0.35)', borderRadius: 2 }} />
              <div style={{ position: 'absolute', left: 14, top: 50, width: 110, height: 6, background: 'rgba(255,255,255,0.18)', borderRadius: 2 }} />
              <div style={{ position: 'absolute', left: 14, bottom: 32, fontSize: 20, fontWeight: 700, color: 'white', letterSpacing: '-0.02em', lineHeight: 1, fontFamily: 'var(--font)' }}>
                {site.name?.split(' ')[0] || 'Site'}
              </div>
              <div style={{ position: 'absolute', left: 14, bottom: 24, width: 80, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 1 }} />
            </>
          )}
        </div>
      )}
      {/* overlay */}
      <div className="site-thumb-overlay">
        <span className="site-status-pill" style={{ background: pillBg }}>
          {sb.dot && <span style={{ width: 6, height: 6, borderRadius: 3, background: 'white', boxShadow: '0 0 6px rgba(255,255,255,0.7)' }} />}
          {sb.label}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {site.status === 'published' && (site.assigned_domain || site.custom_domain) && (
            <a
              href={`https://${site.assigned_domain || site.custom_domain}`}
              target="_blank" rel="noopener noreferrer"
              className="site-action"
              style={{ background: 'rgba(7,8,15,0.5)', borderColor: 'transparent', color: 'white' }}
              title="View live site"
              onClick={e => e.stopPropagation()}
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function SiteCardMore({ site, onDelete, onArchive, onSetPrimary, canSetPrimary }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button className="site-action" title="More" onClick={() => setOpen(v => !v)}>
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setOpen(false)} />
          <div className="card" style={{ position: 'absolute', right: 0, bottom: 'calc(100% + 6px)', zIndex: 10, minWidth: 160, padding: 6, boxShadow: 'var(--shadow-lg)' }}>
            {canSetPrimary && (
              <button
                className="layer-row"
                style={{ width: '100%', gap: 8, padding: '7px 10px', borderRadius: 8, fontSize: 13 }}
                onClick={() => { setOpen(false); onSetPrimary(site) }}
                title="Serve this site at the bare company domain"
              >
                <Star size={13} style={{ color: 'var(--accent-3, #f59e0b)', flexShrink: 0 }} /> Set as primary
              </button>
            )}
            {site.status !== 'archived' && (
              <button
                className="layer-row"
                style={{ width: '100%', gap: 8, padding: '7px 10px', borderRadius: 8, fontSize: 13 }}
                onClick={() => { setOpen(false); onArchive(site) }}
              >
                <Archive size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} /> Archive
              </button>
            )}
            <button
              className="layer-row"
              style={{ width: '100%', gap: 8, padding: '7px 10px', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}
              onClick={() => { setOpen(false); onDelete(site) }}
            >
              <Trash2 size={13} style={{ flexShrink: 0 }} /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function SiteCard({ site, onDelete, onArchive, onSetPrimary, canSetPrimary }) {
  const liveUrl = site.assigned_domain
    ? `https://${site.assigned_domain}`
    : site.custom_domain
    ? `https://${site.custom_domain}`
    : site.azure_published_url || null

  const clientGradNum = ((site.client_id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 6) + 1

  return (
    <div className="site-card">
      <SiteThumb site={site} />
      <div className="site-info">
        {/* Name + client row */}
        <div className="site-name-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="site-name">{site.name}</div>
            <div className="site-client">
              <span className={`avatar avatar-grad-${clientGradNum}`} style={{ width: 18, height: 18, fontSize: 8, flexShrink: 0 }}>
                {(site.company_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </span>
              {site.company_name || '—'}
            </div>
          </div>
        </div>

        {/* Meta: visits · pages · last edited (right) */}
        <div className="site-meta-row">
          <span>{formatVisits(site.visits_30d)}</span>
          <span style={{ color: 'var(--text-faint)' }}>·</span>
          <span>{(site.page_count ?? 0)} {site.page_count === 1 ? 'page' : 'pages'}</span>
          <span style={{ marginLeft: 'auto' }}>{site.updated_at ? formatRelativeTime(site.updated_at) : '—'}</span>
        </div>

        {/* Actions: Edit (flex) | View | Settings | More */}
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <Link to={`/admin/editor?site=${site.id}`} className="btn btn-sm" style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}>
            <Edit2 size={12} strokeWidth={2} /> Edit
          </Link>
          {liveUrl ? (
            <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="site-action" title="View live site">
              <Eye size={13} />
            </a>
          ) : (
            <Link to={`/admin/sites/${site.id}/preview?back=${encodeURIComponent('/admin/sites')}`} className="site-action" title="Preview" style={{ textDecoration: 'none' }}>
              <Eye size={13} />
            </Link>
          )}
          <Link to={`/admin/sites/${site.id}/settings`} className="site-action" title="Settings" style={{ textDecoration: 'none' }}>
            <Settings size={13} />
          </Link>
          <SiteCardMore site={site} onDelete={onDelete} onArchive={onArchive} onSetPrimary={onSetPrimary} canSetPrimary={canSetPrimary} />
        </div>
      </div>
    </div>
  )
}

function AddSiteCard({ onClick }) {
  return (
    <button onClick={onClick} className="site-card" style={{
      border: '1.5px dashed var(--border-strong)',
      background: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 290,
      cursor: 'pointer',
      width: '100%',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div className="icon-tile" style={{ width: 48, height: 48, fontSize: 22, margin: '0 auto 12px' }}>
          <Plus size={20} strokeWidth={1.8} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>New site</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Pick a template or start blank</div>
      </div>
    </button>
  )
}

function SitesTable({ sites, onDelete, onArchive, onSetPrimary, multiSiteClients }) {
  return (
    <div className="sites-table">
      <div className="sites-table-header">
        <div>Site</div>
        <div>Client</div>
        <div>Status</div>
        <div>Pages</div>
        <div>Updated</div>
        <div></div>
      </div>
      {sites.map(site => {
        const [from, to] = siteGradient(site)
        const sb = STATUS_CONFIG[site.status] || STATUS_CONFIG.draft
        return (
          <div className="sites-table-row" key={site.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                background: `linear-gradient(135deg, ${from}, ${to})`,
              }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500, color: 'var(--text)', fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {site.name}
                </div>
                {(site.assigned_domain || site.custom_domain) && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    {site.assigned_domain || site.custom_domain}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="ds-avatar ds-avatar-2" style={{ width: 22, height: 22, fontSize: 9, flexShrink: 0 }}>
                {(site.company_name || '?').slice(0, 2).toUpperCase()}
              </span>
              <span style={{ color: 'var(--text-soft)', fontSize: 13 }}>{site.company_name}</span>
            </div>
            <div>
              <span className={`badge ${sb.tone ? `badge-${sb.tone}` : ''}`}>
                {sb.dot && <span className="badge-dot" />} {sb.label}
              </span>
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-soft)', fontSize: 13 }}>
              {site.page_count || 0}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              {site.updated_at ? formatRelativeTime(site.updated_at) : '—'}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <Link to={`/admin/editor?site=${site.id}`}>
                <button className="site-action" title="Edit"><Edit2 size={13} /></button>
              </Link>
              {site.status === 'published' && (site.assigned_domain || site.custom_domain) && (
                <a href={`https://${site.assigned_domain || site.custom_domain}`} target="_blank" rel="noopener noreferrer">
                  <button className="site-action" title="View live"><ExternalLink size={13} /></button>
                </a>
              )}
              {multiSiteClients?.has(site.client_id) && (
                <button className="site-action" title="Set as primary (serve at the company domain)" onClick={() => onSetPrimary(site)}>
                  <Star size={13} />
                </button>
              )}
              {site.status !== 'archived' && (
                <button className="site-action" title="Archive" onClick={() => onArchive(site)}><Archive size={13} /></button>
              )}
              <button className="site-action" style={{ color: 'var(--danger)' }} title="Delete" onClick={() => onDelete(site)}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* Backed by the shared design-system NSConfirm so every confirmation reads the same.
   `loading` is accepted for backwards-compat; tone now drives icon + colour and
   NSConfirm shows its own busy spinner. `nsIcon` overrides the default tone icon. */
function ConfirmModal({ nsIcon, iconTone, title, message, confirmLabel, confirmClass, requireText, bullets, detail, onConfirm, onCancel }) {
  return (
    <NSConfirm
      tone={iconTone || confirmClass || 'danger'}
      icon={nsIcon}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      requireText={requireText}
      bullets={bullets}
      detail={detail}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  )
}

// ── Main component ────────────────────────────────────────────────────────────

const STATUS_FILTERS = ['all', 'published', 'draft', 'archived']
const STATUS_LABELS = { all: 'All', published: 'Live', draft: 'Draft', archived: 'Archived' }

export default function SiteManager({ openNew = false }) {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [view, setView] = useState('grid')
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [archiveTarget, setArchiveTarget] = useState(null)
  const [showNew, setShowNew] = useState(openNew)
  const closeNew = () => { setShowNew(false); if (openNew) navigate('/admin/sites') }

  const queryClient = useQueryClient()

  const { data: sitesData, isLoading } = useQuery(
    ['sites'],
    () => apiService.get('/sites').then(r => r.data),
    { staleTime: 30000 }
  )

  const allSites = sitesData?.sites || []

  // "Set as primary" only makes sense for clients with more than one site, so
  // collect the client ids that own 2+ sites and gate the action on membership.
  const clientSiteCounts = allSites.reduce((m, s) => { m[s.client_id] = (m[s.client_id] || 0) + 1; return m }, {})
  const multiSiteClients = new Set(Object.keys(clientSiteCounts).filter(id => clientSiteCounts[id] > 1))

  const deleteMutation = useMutation(
    (id) => apiService.delete(`/sites/${id}`),
    {
      onSuccess: () => { toast.success('Site deleted'); queryClient.invalidateQueries(['sites']) },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete site'),
    }
  )

  const archiveMutation = useMutation(
    (id) => apiService.patch(`/sites/${id}/archive`),
    {
      onSuccess: () => { toast.success('Site archived'); queryClient.invalidateQueries(['sites']) },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to archive site'),
    }
  )

  const setPrimaryMutation = useMutation(
    (id) => apiService.post(`/sites/${id}/set-primary`),
    {
      onSuccess: (r) => {
        toast.success(r?.data?.message || 'Site is now the primary site')
        queryClient.invalidateQueries(['sites'])
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to set primary site'),
    }
  )

  const filtered = allSites.filter(s => {
    const matchSearch = !search ||
      (s.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.custom_domain || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.assigned_domain || '').toLowerCase().includes(search.toLowerCase())
    const matchStatus = statusFilter === 'all' || s.status === statusFilter
    return matchSearch && matchStatus
  })

  const counts = {
    all: allSites.length,
    published: allSites.filter(s => s.status === 'published').length,
    draft: allSites.filter(s => s.status === 'draft').length,
    archived: allSites.filter(s => s.status === 'archived').length,
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Sites</div>
          <h1>Sites</h1>
          <div className="subtitle">
            {isLoading
              ? 'Loading sites…'
              : `${counts.all} site${counts.all !== 1 ? 's' : ''} · ${counts.published} live · ${counts.draft} draft`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="ds-btn ds-btn-primary" style={{ gap: 6 }} onClick={() => setShowNew(true)}>
            <Plus size={14} strokeWidth={2} /> New site
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Filter bar */}
        <div className="filter-bar">
          <Search size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            className="ds-input"
            placeholder="Search by name, domain, client…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none', fontSize: 13.5 }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex' }}
            >
              <X size={14} />
            </button>
          )}
          <div className="vdiv" style={{ height: 22 }} />
          <div className="segmented">
            {STATUS_FILTERS.map(k => (
              <button key={k} className={statusFilter === k ? 'on' : ''} onClick={() => setStatusFilter(k)}>
                {STATUS_LABELS[k]}
                <span style={{ color: 'var(--text-faint)', marginLeft: 4, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                  {counts[k]}
                </span>
              </button>
            ))}
          </div>
          <div className="vdiv" style={{ height: 22 }} />
          <div className="segmented">
            <button className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')} title="Grid view">
              <LayoutGrid size={13} />
            </button>
            <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')} title="List view">
              <List size={13} />
            </button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <SkeletonGrid count={6} />
        ) : filtered.length === 0 ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '80px 24px', textAlign: 'center',
          }}>
            <div className="icon-tile" style={{ width: 56, height: 56, fontSize: 24, marginBottom: 16 }}>
              <Globe size={24} strokeWidth={1.5} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              {search || statusFilter !== 'all' ? 'No sites match your filters' : 'No sites yet'}
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 20 }}>
              {search || statusFilter !== 'all'
                ? 'Try adjusting your search or filter.'
                : 'Create your first client site to get started.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {(search || statusFilter !== 'all') && (
                <button className="ds-btn" onClick={() => { setSearch(''); setStatusFilter('all') }}>Clear filters</button>
              )}
              <button className="ds-btn ds-btn-primary" style={{ gap: 5 }} onClick={() => setShowNew(true)}>
                <Plus size={13} /> New site
              </button>
            </div>
          </div>
        ) : view === 'grid' ? (
          <div className="sites-grid stagger-in">
            {filtered.map(s => (
              <SiteCard key={s.id} site={s} onDelete={setDeleteTarget} onArchive={setArchiveTarget} onSetPrimary={(x) => setPrimaryMutation.mutate(x.id)} canSetPrimary={multiSiteClients.has(s.client_id)} />
            ))}
            <AddSiteCard onClick={() => setShowNew(true)} />
          </div>
        ) : (
          <SitesTable sites={filtered} onDelete={setDeleteTarget} onArchive={setArchiveTarget} onSetPrimary={(x) => setPrimaryMutation.mutate(x.id)} multiSiteClients={multiSiteClients} />
        )}
      </div>

      {/* Delete modal */}
      {deleteTarget && (
        <ConfirmModal
          nsIcon="trash"
          iconTone="danger"
          title={`Delete ${deleteTarget.name}?`}
          message="This permanently deletes the site, its pages and media. Visitors will see a 404 immediately."
          detail="This cannot be undone"
          requireText={deleteTarget.name}
          confirmLabel="Delete site"
          confirmClass="danger"
          onConfirm={() => { deleteMutation.mutate(deleteTarget.id); setDeleteTarget(null) }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Archive modal */}
      {archiveTarget && (
        <ConfirmModal
          nsIcon="archive"
          iconTone="warning"
          title={`Archive ${archiveTarget.name}?`}
          message="The site will be hidden from visitors. You can restore it from the archive at any time."
          confirmLabel="Archive"
          confirmClass="warning"
          onConfirm={() => { archiveMutation.mutate(archiveTarget.id); setArchiveTarget(null) }}
          onCancel={() => setArchiveTarget(null)}
        />
      )}

      {/* New site modal */}
      {showNew && <NewSite onClose={closeNew} onCreated={() => queryClient.invalidateQueries(['sites'])} />}
    </>
  )
}
